import { useId } from 'react';
import * as S from '../../domain/sceneOps';
import { BLEND_MODES, EFFECT_RADIUS_MAX, MAX_GRADIENT_STOPS, type BlendMode, type FillGradient, type Glow, type Grade, type GradientStop, type Layer, type Scene, type SceneObject, type Shadow, type Vignette } from '../../domain/schemas';
import { BLEND_LABELS, GLOW_PRESETS, GRADE_PRESETS, GRADIENT_PRESETS, NEUTRAL_GRADE, SHADOW_PRESETS, VIGNETTE_PRESETS, mixHex } from '../../scene/look';
import { useObservable } from '../../lib/useObservable';
import { useServices } from '../../state/services';
import { Button, Panel } from '../ui';
import type { EditFn } from './useSceneEdit';

/**
 * Look v1 editor controls (Phase 5B.1): shape fill gradients, glow and shadow, layer
 * opacity and blend, and the scene grade and vignette. Every change is one `edit` call
 * (so it is validated, undoable and saved like any other edit), and continuous controls
 * pass a coalesce key so dragging a slider is a single undo step.
 */

const sel = 'w-full rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm';
const legend = 'mb-1 text-[11px] font-semibold tracking-wide text-zinc-400 uppercase';
const swatch = 'h-8 w-10 shrink-0 cursor-pointer rounded border border-zinc-700 bg-transparent';

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));
const pct = (v: number) => `${Math.round(v * 100)}%`;
const px = (v: number) => `${Math.round(v)} px`;


/**
 * A labelled range input. Keyboard: arrows step, Page Up/Down jump, Home/End go to the
 * ends (native range behaviour). Values are clamped to the schema's range before commit.
 */
export function Slider({ id, label, value, min, max, step, format = (v) => String(Math.round(v * 100) / 100), onChange, testId }: { id: string; label: string; value: number; min: number; max: number; step: number; format?: (v: number) => string; onChange: (v: number) => void; testId?: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <label htmlFor={id} className="flex justify-between gap-2 text-[11px] text-zinc-400">
        <span>{label}</span>
        <span className="font-mono text-zinc-300">{format(value)}</span>
      </label>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-valuetext={format(value)}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (Number.isFinite(v)) onChange(clamp(v, min, max));
        }}
        className="w-full accent-current"
        data-testid={testId}
      />
    </div>
  );
}

function Swatch({ id, label, value, onChange, testId }: { id: string; label: string; value: string; onChange: (v: string) => void; testId?: string }) {
  return (
    <div className="flex items-center gap-2">
      <input id={id} type="color" value={value} onChange={(e) => onChange(e.target.value)} className={swatch} data-testid={testId} />
      <label htmlFor={id} className="text-xs text-zinc-300">
        {label} <span className="font-mono text-zinc-400">{value}</span>
      </label>
    </div>
  );
}

function Presets<T>({ label, items, onPick, active }: { label: string; items: { id: string; label: string; value: T }[]; onPick: (v: T, label: string) => void; active?: string | null }) {
  return (
    <div className="flex flex-wrap items-center gap-1" role="group" aria-label={label}>
      {items.map((p) => (
        <Button key={p.id} size="sm" aria-pressed={active === p.id} onClick={() => onPick(p.value, p.label)} data-testid={`preset-${label.toLowerCase().replace(/\W+/g, '-')}-${p.id}`}>
          {p.label}
        </Button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- Fill

/** A new stop placed in the widest gap, coloured as the mix of its neighbours. */
function withExtraStop(stops: GradientStop[]): GradientStop[] {
  let at = 1;
  for (let i = 1; i < stops.length; i++) if (stops[i]!.offset - stops[i - 1]!.offset > stops[at]!.offset - stops[at - 1]!.offset) at = i;
  const a = stops[at - 1]!;
  const b = stops[at]!;
  const stop = { offset: Math.round(((a.offset + b.offset) / 2) * 100) / 100, color: mixHex(a.color, b.color, 0.5), opacity: (a.opacity + b.opacity) / 2 };
  return [...stops.slice(0, at), stop, ...stops.slice(at)];
}

/**
 * Fill: solid colour, or a linear/radial gradient in the shape's own box. The solid fill
 * is kept underneath (and comes back when the gradient is removed).
 */
export function FillEditor({ object, edit, sceneId }: { object: Extract<SceneObject, { style: unknown }>; edit: EditFn; sceneId: string }) {
  const f = useId();
  const g = object.style.gradient ?? null;
  const set = (label: string, gradient: FillGradient | null, key: string) => edit(label, (p) => S.updateObjects(p, sceneId, [object.id], { style: { gradient } } as S.ObjectPatch), `${object.id}:gradient.${key}`);
  const setStop = (i: number, patch: Partial<GradientStop>, key: string) => {
    if (!g) return;
    const stops = g.stops.map((s, j) => (j === i ? { ...s, ...patch } : s));
    // Offsets stay ordered: a stop cannot be dragged past its neighbours.
    if (patch.offset !== undefined) stops[i]!.offset = clamp(patch.offset, stops[i - 1]?.offset ?? 0, stops[i + 1]?.offset ?? 1);
    set('Gradient stop', { ...g, stops }, `stop${i}.${key}`);
  };
  const kind = g?.type ?? 'solid';
  return (
    <div className="flex flex-col gap-2" data-testid="fill-editor">
      <div className="flex flex-col gap-0.5">
        <label htmlFor={`${f}-type`} className="text-[11px] text-zinc-400">
          Fill
        </label>
        <select
          id={`${f}-type`}
          className={sel}
          value={kind}
          onChange={(e) => {
            const v = e.target.value;
            const stops = g?.stops ?? [{ offset: 0, color: mixHex(object.style.fill, '#ffffff', 0.4), opacity: 1 }, { offset: 1, color: mixHex(object.style.fill, '#000000', 0.4), opacity: 1 }];
            set('Fill type', v === 'solid' ? null : v === 'linear' ? { type: 'linear', angle: 90, stops } : { type: 'radial', cx: 0.5, cy: 0.5, radius: 0.5, stops }, 'type');
          }}
          data-testid="fill-type"
        >
          <option value="solid">Solid colour</option>
          <option value="linear">Linear gradient</option>
          <option value="radial">Radial gradient</option>
        </select>
      </div>
      <Presets label="Gradient presets" items={GRADIENT_PRESETS.map((p) => ({ id: p.id, label: p.label, value: p.make(object.style.fill) }))} onPick={(v, label) => set(`Gradient: ${label}`, v, 'preset')} />
      {g && (
        <>
          {g.type === 'linear' ? (
            <Slider id={`${f}-angle`} label="Direction" value={g.angle} min={0} max={360} step={5} format={(v) => `${Math.round(v)}°`} onChange={(v) => set('Gradient direction', { ...g, angle: v }, 'angle')} testId="gradient-angle" />
          ) : (
            <div className="grid grid-cols-3 gap-2">
              <Slider id={`${f}-cx`} label="Centre X" value={g.cx} min={0} max={1} step={0.05} format={pct} onChange={(v) => set('Gradient centre', { ...g, cx: v }, 'cx')} />
              <Slider id={`${f}-cy`} label="Centre Y" value={g.cy} min={0} max={1} step={0.05} format={pct} onChange={(v) => set('Gradient centre', { ...g, cy: v }, 'cy')} />
              <Slider id={`${f}-r`} label="Radius" value={g.radius} min={0.05} max={1.5} step={0.05} format={pct} onChange={(v) => set('Gradient radius', { ...g, radius: v }, 'radius')} />
            </div>
          )}
          <ol className="flex flex-col gap-2" aria-label="Gradient stops">
            {g.stops.map((s, i) => (
              <li key={i} className="flex flex-col gap-1 rounded-md border border-zinc-800 p-2" data-testid="gradient-stop">
                <div className="flex items-center justify-between gap-2">
                  <Swatch id={`${f}-c${i}`} label={`Stop ${i + 1}`} value={s.color} onChange={(v) => setStop(i, { color: v }, 'color')} />
                  <Button size="sm" variant="ghost" aria-label={`Remove stop ${i + 1}`} disabled={g.stops.length <= 2} onClick={() => set('Remove gradient stop', { ...g, stops: g.stops.filter((_, j) => j !== i) }, `remove${i}`)}>
                    ✕
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Slider id={`${f}-o${i}`} label="Position" value={s.offset} min={0} max={1} step={0.01} format={pct} onChange={(v) => setStop(i, { offset: v }, 'offset')} />
                  <Slider id={`${f}-a${i}`} label="Opacity" value={s.opacity} min={0} max={1} step={0.05} format={pct} onChange={(v) => setStop(i, { opacity: v }, 'opacity')} />
                </div>
              </li>
            ))}
          </ol>
          <Button size="sm" disabled={g.stops.length >= MAX_GRADIENT_STOPS} onClick={() => set('Add gradient stop', { ...g, stops: withExtraStop(g.stops) }, 'add')} data-testid="gradient-add-stop">
            + Stop {g.stops.length >= MAX_GRADIENT_STOPS ? `(max ${MAX_GRADIENT_STOPS})` : ''}
          </Button>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Glow & shadow

/** The colour a glow starts from: the object's own fill or text colour. */
const mainColour = (o: SceneObject): string => ('style' in o ? o.style.fill : o.type === 'text' ? o.color : '#ffffff');

/**
 * Glow and drop shadow for the selection (applied to every editable selected object; the
 * fields show the first object's values). The per-scene effect budget is enforced by the
 * scene schema, so going over it shows the edit error instead of applying.
 */
export function EffectsEditor({ objects, ids, edit, sceneId }: { objects: SceneObject[]; ids: string[]; edit: EditFn; sceneId: string }) {
  const f = useId();
  const first = objects[0]!;
  const glow = objects.find((o) => o.glow)?.glow ?? null;
  const shadow = objects.find((o) => o.shadow)?.shadow ?? null;
  const key = ids.join(',');
  const setGlow = (label: string, v: Glow | null, k: string) => edit(label, (p) => S.updateObjects(p, sceneId, ids, { glow: v }), `${key}:glow.${k}`);
  const setShadow = (label: string, v: Shadow | null, k: string) => edit(label, (p) => S.updateObjects(p, sceneId, ids, { shadow: v }), `${key}:shadow.${k}`);
  return (
    <fieldset className="flex flex-col gap-3" data-testid="effects-editor">
      <legend className={legend}>Glow &amp; shadow</legend>
      <div className="flex flex-col gap-2">
        <label className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" checked={!!glow} onChange={(e) => setGlow(e.target.checked ? 'Add glow' : 'Remove glow', e.target.checked ? GLOW_PRESETS[0]!.make(mainColour(first)) : null, 'toggle')} data-testid="glow-toggle" />
          Glow
        </label>
        {glow && (
          <>
            <Presets label="Glow presets" items={GLOW_PRESETS.map((p) => ({ id: p.id, label: p.label, value: p.make(glow.color) }))} onPick={(v, label) => setGlow(`Glow: ${label}`, v, 'preset')} />
            <Swatch id={`${f}-gc`} label="Glow colour" value={glow.color} onChange={(v) => setGlow('Glow colour', { ...glow, color: v }, 'color')} />
            <div className="grid grid-cols-2 gap-2">
              <Slider id={`${f}-gr`} label="Size" value={glow.radius} min={1} max={EFFECT_RADIUS_MAX} step={1} format={px} onChange={(v) => setGlow('Glow size', { ...glow, radius: Math.round(v) }, 'radius')} testId="glow-radius" />
              <Slider id={`${f}-gs`} label="Strength" value={glow.strength} min={0} max={1} step={0.05} format={pct} onChange={(v) => setGlow('Glow strength', { ...glow, strength: v }, 'strength')} />
            </div>
          </>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <label className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" checked={!!shadow} onChange={(e) => setShadow(e.target.checked ? 'Add shadow' : 'Remove shadow', e.target.checked ? SHADOW_PRESETS[0]!.make('#000000') : null, 'toggle')} data-testid="shadow-toggle" />
          Shadow
        </label>
        {shadow && (
          <>
            <Presets label="Shadow presets" items={SHADOW_PRESETS.map((p) => ({ id: p.id, label: p.label, value: { ...p.make('#000000'), color: shadow.color } }))} onPick={(v, label) => setShadow(`Shadow: ${label}`, v, 'preset')} />
            <Swatch id={`${f}-sc`} label="Shadow colour" value={shadow.color} onChange={(v) => setShadow('Shadow colour', { ...shadow, color: v }, 'color')} />
            <div className="grid grid-cols-2 gap-2">
              <Slider id={`${f}-sb`} label="Blur" value={shadow.blur} min={0} max={EFFECT_RADIUS_MAX} step={1} format={px} onChange={(v) => setShadow('Shadow blur', { ...shadow, blur: Math.round(v) }, 'blur')} testId="shadow-blur" />
              <Slider id={`${f}-so`} label="Opacity" value={shadow.opacity} min={0} max={1} step={0.05} format={pct} onChange={(v) => setShadow('Shadow opacity', { ...shadow, opacity: v }, 'opacity')} />
              <Slider id={`${f}-sx`} label="Offset X" value={shadow.offsetX} min={-200} max={200} step={1} format={px} onChange={(v) => setShadow('Shadow offset', { ...shadow, offsetX: Math.round(v) }, 'offsetX')} />
              <Slider id={`${f}-sy`} label="Offset Y" value={shadow.offsetY} min={-200} max={200} step={1} format={px} onChange={(v) => setShadow('Shadow offset', { ...shadow, offsetY: Math.round(v) }, 'offsetY')} />
            </div>
          </>
        )}
      </div>
    </fieldset>
  );
}

// ---------------------------------------------------------------- Layers

/** Opacity and blend mode of one layer (applied to the whole layer, on top of each object's opacity). */
export function LayerLookControls({ layer, edit, sceneId }: { layer: Layer; edit: EditFn; sceneId: string }) {
  const f = useId();
  return (
    <div className="grid grid-cols-2 items-end gap-2 border-t border-zinc-800 px-2 py-2" data-testid="layer-look">
      <Slider id={`${f}-op`} label="Layer opacity" value={layer.opacity ?? 1} min={0} max={1} step={0.05} format={pct} onChange={(v) => edit('Layer opacity', (p) => S.updateLayer(p, sceneId, layer.id, { opacity: v }), `${layer.id}:opacity`)} testId="layer-opacity" />
      <div className="flex flex-col gap-0.5">
        <label htmlFor={`${f}-blend`} className="text-[11px] text-zinc-400">
          Blend
        </label>
        <select id={`${f}-blend`} className={sel} value={layer.blend ?? 'normal'} onChange={(e) => edit('Layer blend', (p) => S.updateLayer(p, sceneId, layer.id, { blend: e.target.value as BlendMode }))} data-testid="layer-blend">
          {BLEND_MODES.map((m) => (
            <option key={m} value={m}>
              {BLEND_LABELS[m]}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Scene look

const sameGrade = (a: Grade, b: Grade) => a.brightness === b.brightness && a.contrast === b.contrast && a.saturation === b.saturation && a.tint.toLowerCase() === b.tint.toLowerCase() && a.tintAmount === b.tintAmount;
const sameVignette = (a: Vignette, b: Vignette) => a.amount === b.amount && a.softness === b.softness && a.color.toLowerCase() === b.color.toLowerCase();

/**
 * Scene finishing: colour grade and vignette, applied over the whole scene in the preview
 * and the export alike (scene-relative, so it looks the same at any output size).
 */
export function SceneLookPanel({ scene, edit }: { scene: Scene; edit: EditFn }) {
  const f = useId();
  const sid = scene.id;
  const grade = scene.look?.grade ?? null;
  const vignette = scene.look?.vignette ?? null;
  const g = grade ?? NEUTRAL_GRADE;
  const setGrade = (label: string, v: Grade | null, k: string) => edit(label, (p) => S.setSceneLook(p, sid, { grade: v }), `${sid}:grade.${k}`);
  const setVignette = (label: string, v: Vignette | null, k: string) => edit(label, (p) => S.setSceneLook(p, sid, { vignette: v }), `${sid}:vignette.${k}`);
  const activeGrade = GRADE_PRESETS.find((p) => sameGrade(p.grade, g))?.id ?? null;
  const activeVignette = vignette ? (VIGNETTE_PRESETS.find((p) => sameVignette(p.vignette, vignette))?.id ?? null) : null;
  const { animation: bus } = useServices();
  const draft = useObservable(bus.draftEffects);
  return (
    <Panel
      title="Look"
      headingLevel={3}
      actions={
        <Button size="sm" variant="ghost" disabled={!scene.look} onClick={() => edit('Reset look', (p) => S.setSceneLook(p, sid, { grade: null, vignette: null }))} data-testid="look-reset">
          Reset
        </Button>
      }
    >
      <div className="flex flex-col gap-3" data-testid="scene-look">
        <fieldset className="flex flex-col gap-2">
          <legend className={legend}>Colour grade</legend>
          <Presets label="Grade presets" items={GRADE_PRESETS.map((p) => ({ id: p.id, label: p.label, value: p.grade }))} active={activeGrade} onPick={(v, label) => setGrade(`Grade: ${label}`, v === NEUTRAL_GRADE ? null : v, 'preset')} />
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-2">
            <Slider id={`${f}-b`} label="Brightness" value={g.brightness} min={0} max={2} step={0.05} format={pct} onChange={(v) => setGrade('Brightness', { ...g, brightness: v }, 'brightness')} testId="grade-brightness" />
            <Slider id={`${f}-c`} label="Contrast" value={g.contrast} min={0} max={2} step={0.05} format={pct} onChange={(v) => setGrade('Contrast', { ...g, contrast: v }, 'contrast')} testId="grade-contrast" />
            <Slider id={`${f}-s`} label="Saturation" value={g.saturation} min={0} max={2} step={0.05} format={pct} onChange={(v) => setGrade('Saturation', { ...g, saturation: v }, 'saturation')} testId="grade-saturation" />
            <Slider id={`${f}-ta`} label="Tint amount" value={g.tintAmount} min={0} max={1} step={0.05} format={pct} onChange={(v) => setGrade('Tint', { ...g, tintAmount: v }, 'tintAmount')} />
          </div>
          <Swatch id={`${f}-t`} label="Tint" value={g.tint} onChange={(v) => setGrade('Tint', { ...g, tint: v, tintAmount: g.tintAmount || 0.2 }, 'tint')} />
        </fieldset>
        <fieldset className="flex flex-col gap-2">
          <legend className={legend}>Vignette</legend>
          <label className="flex items-center gap-1.5 text-sm">
            <input type="checkbox" checked={!!vignette} onChange={(e) => setVignette(e.target.checked ? 'Add vignette' : 'Remove vignette', e.target.checked ? VIGNETTE_PRESETS[0]!.vignette : null, 'toggle')} data-testid="vignette-toggle" />
            Darken the edges
          </label>
          {vignette && (
            <>
              <Presets label="Vignette presets" items={VIGNETTE_PRESETS.map((p) => ({ id: p.id, label: p.label, value: { ...p.vignette, color: vignette.color } }))} active={activeVignette} onPick={(v, label) => setVignette(`Vignette: ${label}`, v, 'preset')} />
              <div className="grid grid-cols-2 gap-2">
                <Slider id={`${f}-va`} label="Amount" value={vignette.amount} min={0} max={1} step={0.05} format={pct} onChange={(v) => setVignette('Vignette amount', { ...vignette, amount: v }, 'amount')} testId="vignette-amount" />
                <Slider id={`${f}-vs`} label="Softness" value={vignette.softness} min={0} max={1} step={0.05} format={pct} onChange={(v) => setVignette('Vignette softness', { ...vignette, softness: v }, 'softness')} />
              </div>
              <Swatch id={`${f}-vc`} label="Vignette colour" value={vignette.color} onChange={(v) => setVignette('Vignette colour', { ...vignette, color: v }, 'color')} />
            </>
          )}
        </fieldset>
        <label className="flex items-start gap-1.5 text-sm">
          <input type="checkbox" className="mt-1" checked={draft} onChange={(e) => bus.setDraftEffects(e.target.checked)} data-testid="draft-effects" />
          <span>
            Draft effects in previews
            <span className="block text-xs text-zinc-400">Faster playback with many glows and shadows. Video export always uses full quality.</span>
          </span>
        </label>
      </div>
    </Panel>
  );
}
